import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Bell, CalendarClock, Package, X, Droplets, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import { usePushNotification } from '@/hooks/usePushNotification';
// ASA alerts disabled - uncomment to re-enable:
// import { useAsaAlerts } from '@/hooks/useAsaAlerts';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // ASA alerts disabled - uncomment to re-enable:
  // const { alerts: asaAlerts, dismissAlert: dismissAsaAlert } = useAsaAlerts();
  const asaAlerts = []; // stub saat ASA dinonaktifkan
  
  const { isSupported, isSubscribed, isLoading, subscribeToPush } = usePushNotification();
  const [dismissedStdAlerts, setDismissedStdAlerts] = useState([]);
  const { member } = useSession();

  // Load from DB on mount
  React.useEffect(() => {
    if (!member) return;
    try {
      const stored = typeof member.dismissed_std_alerts === 'string' 
        ? JSON.parse(member.dismissed_std_alerts) 
        : (member.dismissed_std_alerts || []);
        
      const now = Date.now();
      const valid = stored.filter(item => typeof item === 'object' && item.expiresAt > now);
      
      if (valid.length !== stored.length) {
        // Clean up expired ones in DB
        base44.entities.TeamMember.update(member.id, { dismissed_std_alerts: JSON.stringify(valid) }).catch(()=>{});
      }
      setDismissedStdAlerts(valid.map(item => item.id));
    } catch { setDismissedStdAlerts([]); }
  }, [member]);

  const dismissStdAlert = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!member) return;
    
    setDismissedStdAlerts(prev => [...prev, id]);
    
    // Simpan ke DB dengan waktu kedaluwarsa 24 jam (besok muncul lagi)
    try {
      const freshMember = await base44.entities.TeamMember.get(member.id);
      const stored = typeof freshMember?.dismissed_std_alerts === 'string' 
        ? JSON.parse(freshMember.dismissed_std_alerts) 
        : (freshMember?.dismissed_std_alerts || []);
        
      const valid = stored.filter(item => typeof item === 'object' && item.expiresAt > Date.now());
      valid.push({ id, expiresAt: Date.now() + 24 * 60 * 60 * 1000 });
      
      await base44.entities.TeamMember.update(member.id, { dismissed_std_alerts: JSON.stringify(valid) });
    } catch (err) { console.error('Failed to save dismissed alert to DB', err); }
  };
  
  const isFinance = location.pathname.startsWith('/office/finance');
  const isProduction = location.pathname.startsWith('/office/production');
  const isMarketing = location.pathname.startsWith('/office/marketing');
  const isPurchasing = location.pathname.startsWith('/office/purchasing');

  const STALE = 5 * 60 * 1000; // 5 menit

  const { data: orders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list('order_number'),
    staleTime: STALE,
  });

  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list(), staleTime: STALE });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list(), staleTime: STALE });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list(), staleTime: STALE });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list(), staleTime: STALE });

  // Query Real Database Notifications (Generated by Smart Notification Engine)
  const { data: dbNotifications = [], refetch: refetchDbNotifs } = useQuery({
    queryKey: ['db-notifications-bell', member?.id],
    queryFn: async () => {
      if (!member?.id) return [];
      const list = await base44.entities.Notification.list('-created_at');
      return list.filter(n => {
        if (n.recipient_id === String(member.id)) return true;
        if (n.recipient_role && n.recipient_role === member.role) return true;
        if (['owner', 'developer'].includes(member.role)) return true;
        return false;
      });
    },
    staleTime: 5000,
    refetchInterval: 10000,
  });

  const unreadDbCount = dbNotifications.filter(n => !n.is_read).length;

  const markNotifRead = async (notif) => {
    try {
      if (!notif.is_read) {
        await base44.entities.Notification.update(notif.id, { is_read: true });
        refetchDbNotifs();
      }
      if (notif.action_url) {
        setOpen(false);
        navigate(notif.action_url);
      }
    } catch (e) {
      console.warn('Gagal update status read:', e);
    }
  };

  const today = new Date();
  const in3Days = new Date(today); in3Days.setDate(today.getDate() + 3);
  const in7Days = new Date(today); in7Days.setDate(today.getDate() + 7);

  // Deadline Mendekat
  // Filter out trashed items first (safety net against cached data)
  const liveOrders = orders.filter(o => !o.deleted_at);
  const liveBahan = bahanList.filter(i => !i.deleted_at);
  const liveBottol = botolList.filter(i => !i.deleted_at);
  const liveTutup = tutupList.filter(i => !i.deleted_at);
  const liveSpray = sprayList.filter(i => !i.deleted_at);

  const deadlineAlerts = liveOrders
    .filter(o => !o.is_shipped && o.status !== 'done' && o.deadline)
    .map(o => ({ ...o, deadlineDate: new Date(o.deadline) }))
    .filter(o => o.deadlineDate >= today && o.deadlineDate <= in7Days && !dismissedStdAlerts.includes(`dl_${o.id}`))
    .sort((a, b) => a.deadlineDate - b.deadlineDate);

  // PO Shortages (Barang Harus Dibeli untuk PO)
  const ARRIVED_STATUSES = ['sudah_datang', 'selesai'];
  const PENDING_PO_STATUSES = ['sudah_po', 'sudah_order', 'dalam_pengiriman', 'dalam_perjalanan'];

  const activeOrders = liveOrders.filter(o => !o.is_shipped && o.status !== 'done');
  
  const poShortages = activeOrders.map(o => {
    const components = o.components || [];
    const pStatuses = o.purchase_statuses || {};
    const shortages = components.filter(c => 
      c.inventory_item_id && 
      c.status === 'need_buy' && 
      !ARRIVED_STATUSES.includes(pStatuses[c.inventory_item_id]) && 
      !PENDING_PO_STATUSES.includes(pStatuses[c.inventory_item_id])
    );
    
    // Group shortages by inventory_item_id to aggregate qtys
    const grouped = shortages.reduce((acc, curr) => {
       if (!acc[curr.inventory_item_id]) {
          acc[curr.inventory_item_id] = { ...curr, aggregated_qty_needed: 0 };
       }
       acc[curr.inventory_item_id].aggregated_qty_needed += (curr.qty_needed || 0);
       return acc;
    }, {});

    return {
       ...o,
       shortages: Object.values(grouped)
    };
  }).filter(o => o.shortages.length > 0 && !dismissedStdAlerts.includes(`short_${o.id}`));

  // Bahan cair stok < 1kg
  const lowBahan = liveBahan.filter(i => i.stok != null && i.stok < 1 && !dismissedStdAlerts.includes(`bahan_${i.id}`));

  // Packaging stok < 100 pcs
  const packagingAll = [
    ...liveBottol.map(i => ({ ...i, _tipe: 'botol' })),
    ...liveTutup.map(i => ({ ...i, _tipe: 'tutup' })),
    ...liveSpray.map(i => ({ ...i, _tipe: 'spray' })),
  ];
  const lowPackaging = packagingAll.filter(i => i.stok != null && i.stok < 100 && !dismissedStdAlerts.includes(`pack_${i.id}_${i._tipe}`));

  // Hitung total notifikasi sesuai portal + notifikasi DB
  let total = unreadDbCount;
  if (isFinance) total += poShortages.length + lowBahan.length + lowPackaging.length;
  else if (isProduction) total += deadlineAlerts.length + poShortages.length + lowBahan.length + lowPackaging.length;
  else if (isMarketing) total += deadlineAlerts.length + lowBahan.length + lowPackaging.length;
  else if (isPurchasing) total += poShortages.length + lowBahan.length + lowPackaging.length;

  const formatDate = (d) => d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
  const daysLeft = (d) => {
    const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Hari ini!';
    if (diff === 1) return 'Besok!';
    return `${diff} hari lagi`;
  };

  const goToStok = (tab) => {
    setOpen(false);
    navigate(`/office/${isFinance ? 'finance' : 'production'}/inventory?tab=${tab}`);
  };

  const portalName = isFinance ? 'Finance' : isProduction ? 'Produksi' : isMarketing ? 'Marketing' : isPurchasing ? 'Purchasing' : 'Sistem';

  return (
    <div className="relative">
      <Button variant="ghost" size="icon" onClick={() => setOpen(o => !o)} className="relative hover:bg-slate-100 rounded-full w-8 h-8 md:w-10 md:h-10 shrink-0 transition-colors text-slate-600">
        <Bell size={16} className="md:w-5 md:h-5 stroke-[1.5]" />
        {total > 0 && (
          <span className="absolute top-0 right-0 md:top-1 md:right-1 w-[14px] h-[14px] md:w-4 md:h-4 rounded-full bg-[#C9A227] text-white text-[8px] md:text-[10px] flex items-center justify-center font-bold border border-white leading-none shadow-xs">
            {total > 9 ? '9+' : total}
          </span>
        )}
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-50 w-84 sm:w-96 rounded-2xl border border-[#E6D5B8]/60 bg-[#FFFDF9] shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6D5B8]/40 bg-white/50 rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#C9A227]" />
                <h3 className="font-bold text-xs text-[#302621]">Notifikasi Satala ({portalName})</h3>
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-[#7A695B]" onClick={() => setOpen(false)}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>

            <div className="max-h-[480px] overflow-y-auto p-2 space-y-2">
              {total === 0 && dbNotifications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <p className="text-xs">Semua aman & tidak ada notifikasi baru 👍</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* PWA Push Notification Prompt */}
                  {isSupported && !isSubscribed && (
                    <div className="bg-[#FFFDF9] border border-[#8C6208]/30 rounded-xl p-3 mb-2 shadow-2xs">
                      <p className="text-xs font-bold text-[#302621] mb-1 flex items-center gap-1.5">
                        <Bell className="w-3.5 h-3.5 text-[#C9A227]" /> Notifikasi Handphone
                      </p>
                      <p className="text-[10px] text-slate-500 mb-2 leading-tight">Aktifkan agar tugas dan info dari web bisa langsung masuk ke HP kamu seperti aplikasi asli.</p>
                      <Button 
                        size="sm" 
                        onClick={subscribeToPush} 
                        disabled={isLoading}
                        className="w-full h-7 text-[10px] font-bold bg-[#8C6208] hover:bg-[#6b4a06] text-white"
                      >
                        {isLoading ? 'Mengaktifkan...' : 'Aktifkan Sekarang'}
                      </Button>
                    </div>
                  )}

                  {/* REAL IN-APP NOTIFICATIONS (From Smart Notification Engine) */}
                  {dbNotifications.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between px-1">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                          Pemberitahuan Sistem
                        </span>
                        {unreadDbCount > 0 && (
                          <span className="text-[9px] text-[#C9A227] font-semibold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                            {unreadDbCount} Belum Dibaca
                          </span>
                        )}
                      </div>

                      {dbNotifications.map((notif) => {
                        const notifTime = notif.created_at 
                          ? new Date(notif.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) 
                          : 'Baru';
                        return (
                          <div
                            key={notif.id}
                            onClick={() => markNotifRead(notif)}
                            className={`p-3 rounded-2xl bg-white border transition-all cursor-pointer flex items-start gap-3 shadow-xs hover:border-[#C9A227]/50 ${
                              !notif.is_read ? 'border-[#C9A227]/40 bg-amber-50/20' : 'border-[#E6D5B8]/40'
                            }`}
                          >
                            {/* Left: Satala Logo in rounded square box */}
                            <div className="w-10 h-10 rounded-xl bg-white border border-[#E6D5B8]/50 p-1.5 shadow-2xs flex items-center justify-center shrink-0">
                              <img src="/logo.png" alt="Satala" className="w-full h-full object-contain" />
                            </div>

                            {/* Center: Content */}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-foreground leading-tight truncate">
                                {notif.title}
                              </p>
                              <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                                {notif.body}
                              </p>
                              <div className="mt-1.5 flex items-center gap-1.5">
                                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase border ${notif.category_color || 'bg-amber-50 text-amber-900 border-amber-200'}`}>
                                  {notif.category || 'FINANCE'}
                                </span>
                              </div>
                            </div>

                            {/* Right: Time & Unread Status Dot */}
                            <div className="flex flex-col items-end justify-between self-stretch gap-2 shrink-0 pt-0.5">
                              <span className="text-[10px] text-muted-foreground font-medium">
                                {notifTime}
                              </span>
                              {!notif.is_read ? (
                                <span className="w-2 h-2 rounded-full bg-[#C9A227] animate-pulse" />
                              ) : (
                                <span className="w-2 h-2" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ASA Alerts - DISABLED
                  {asaAlerts.length > 0 && (...)} 
                  */}

                  {/* Deadline Mendekat (Production & Marketing) */}
                  {(isProduction || isMarketing) && deadlineAlerts.length > 0 && (
                    <>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 pt-1 flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" /> Deadline Mendekat ({deadlineAlerts.length})
                      </p>
                      {deadlineAlerts.map(o => (
                        <div key={`dl-${o.id}`} className="relative group">
                          <Link to={`/office/${isMarketing ? 'marketing' : 'production'}/invoices`} onClick={() => setOpen(false)}>
                            <div className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-accent transition-colors cursor-pointer pr-8">
                              <CalendarClock className={`w-4 h-4 mt-0.5 shrink-0 ${o.deadlineDate <= in3Days ? 'text-destructive' : 'text-yellow-600'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{o.brand_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatDate(o.deadlineDate)} ·{' '}
                                  <span className={o.deadlineDate <= in3Days ? 'text-destructive font-medium' : 'text-yellow-600 font-medium'}>
                                    {daysLeft(o.deadlineDate)}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </Link>
                          <button 
                            onClick={(e) => dismissStdAlert(e, `dl_${o.id}`)}
                            className="absolute top-2 right-2 p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Kebutuhan PO (Barang Harus Dibeli) - (Finance, Production, Purchasing) */}
                  {(isFinance || isProduction || isPurchasing) && poShortages.length > 0 && (
                    <>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 pt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-600" /> Kebutuhan PO / Harus Beli ({poShortages.length})
                      </p>
                      {poShortages.map(o => (
                        <div key={`short-${o.id}`} className="relative group">
                          <Link to={`/office/${isFinance ? 'finance/invoices' : `production/${o.id}`}`} onClick={() => setOpen(false)}>
                            <div className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-red-50/50 transition-colors cursor-pointer border border-transparent hover:border-red-100 pr-8">
                              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-red-800 truncate leading-tight">PO #{o.order_number} {o.brand_name}</p>
                                <div className="mt-1 space-y-1">
                                  {o.shortages.map((s, idx) => {
                                    const displayQty = s.aggregated_qty_needed >= 1000 && (s.unit === 'ml' || s.unit === 'g' || s.unit === 'gram') 
                                      ? `${(s.aggregated_qty_needed / 1000).toFixed(2)} ${s.unit === 'ml' ? 'L' : 'kg'}` 
                                      : `${s.aggregated_qty_needed} ${s.unit}`;
                                    
                                    return (
                                      <div key={idx} className="flex justify-between items-center text-[11px]">
                                        <span className="text-red-700 font-medium truncate pr-2">• {s.item_name || s.name}</span>
                                        <span className="bg-red-100 text-red-700 px-1.5 rounded font-bold shrink-0">Beli {displayQty}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </Link>
                          <button 
                            onClick={(e) => dismissStdAlert(e, `short_${o.id}`)}
                            className="absolute top-2 right-2 p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Bahan Cair Kritis < 1kg (Semua Portal terkait Inventori) */}
                  {(isProduction || isFinance || isMarketing || isPurchasing) && lowBahan.length > 0 && (
                    <>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 pt-2 flex items-center gap-1">
                        <Droplets className="w-3 h-3 text-blue-600" /> Bahan Cair Menipis ({lowBahan.length})
                      </p>
                      {lowBahan.map(i => (
                        <div key={i.id} className="relative group">
                          <button
                            type="button"
                            onClick={() => goToStok('bahan-cair')}
                            className="w-full text-left flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-accent transition-colors pr-8"
                          >
                            <Droplets className={`w-4 h-4 mt-0.5 shrink-0 ${i.stok <= 0 ? 'text-destructive' : 'text-blue-600'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{i.nama}</p>
                              <p className="text-xs text-muted-foreground">
                                {i.kategori} ·{' '}
                                <span className={i.stok <= 0 ? 'text-destructive font-medium' : 'text-blue-600 font-medium'}>
                                  {i.stok <= 0 ? 'Habis!' : `Sisa ${i.stok} ${i.satuan || 'kg'} (batas 1kg)`}
                                </span>
                              </p>
                            </div>
                          </button>
                          <button 
                            onClick={(e) => dismissStdAlert(e, `bahan_${i.id}`)}
                            className="absolute top-2 right-2 p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Packaging Kritis < 100 pcs (Semua Portal terkait Inventori) */}
                  {(isProduction || isFinance || isMarketing || isPurchasing) && lowPackaging.length > 0 && (
                    <>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase px-2 pt-2 flex items-center gap-1">
                        <Package className="w-3 h-3 text-orange-500" /> Packaging Menipis ({lowPackaging.length})
                      </p>
                      {lowPackaging.map(i => (
                        <div key={i.id + i._tipe} className="relative group">
                          <button
                            type="button"
                            onClick={() => goToStok(i._tipe)}
                            className="w-full text-left flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-accent transition-colors pr-8"
                          >
                            <Package className={`w-4 h-4 mt-0.5 shrink-0 ${i.stok <= 0 ? 'text-destructive' : 'text-orange-500'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{i.nama}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {i._tipe} ·{' '}
                                <span className={i.stok <= 0 ? 'text-destructive font-medium' : 'text-orange-500 font-medium'}>
                                  {i.stok <= 0 ? 'Habis!' : `Sisa ${i.stok} pcs (batas 100 pcs)`}
                                </span>
                              </p>
                            </div>
                          </button>
                          <button 
                            onClick={(e) => dismissStdAlert(e, `pack_${i.id}_${i._tipe}`)}
                            className="absolute top-2 right-2 p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
              
              {/* Tombol Lihat Detail di bawah popover (selalu tampil) */}
              <div className="p-2 border-t border-[#E6D5B8]/40 bg-gray-50/50 rounded-b-xl">
                <Button 
                  variant="outline" 
                  className="w-full text-xs font-semibold h-8 border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC]"
                  onClick={() => { setOpen(false); setDetailOpen(true); }}
                >
                  Lihat Detail Lengkap
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Slide Bar (Sheet) untuk Detail Lengkap */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg md:max-w-xl p-0 flex flex-col bg-[#FCFAF5] border-l border-[#E6D5B8]/60">
          <SheetHeader className="px-6 py-4 border-b border-[#E6D5B8]/40 bg-white shadow-xs sticky top-0 z-10">
            <SheetTitle className="text-xl font-display text-[#302621]">Detail Notifikasi {portalName}</SheetTitle>
            <SheetDescription>
              Detail komprehensif mengenai peringatan sistem dan kebutuhan stok.
            </SheetDescription>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* ASA Alerts - DISABLED
            {asaAlerts.length > 0 && (...)} 
            */}

            {/* Kebutuhan PO (Barang Harus Dibeli) */}
            {(isFinance || isProduction || isPurchasing) && poShortages.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h4 className="font-bold text-red-800 text-sm uppercase">Kebutuhan Produksi (PO) - Segera Beli</h4>
                </div>
                {poShortages.map(o => (
                  <div key={`sheet-short-${o.id}`} className="bg-white border border-red-200 rounded-xl p-4 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="text-xs font-mono text-red-600/70 font-semibold mb-1 block">PO #{o.order_number}</span>
                        <h5 className="font-bold text-[#302621] text-lg leading-tight">{o.brand_name}</h5>
                      </div>
                      <Button variant="outline" size="sm" className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50" onClick={() => { setDetailOpen(false); navigate(`/office/${isFinance ? 'finance/invoices' : `production/${o.id}`}`); }}>
                        Buka PO
                      </Button>
                    </div>
                    
                    <div className="bg-red-50/50 rounded-lg p-3 border border-red-100">
                      <p className="text-xs font-semibold text-red-800 mb-2 border-b border-red-100 pb-1">Daftar Kebutuhan yang Belum Terpenuhi:</p>
                      <ul className="space-y-2">
                        {o.shortages.map((s, idx) => {
                          const displayQty = s.aggregated_qty_needed >= 1000 && (s.unit === 'ml' || s.unit === 'g' || s.unit === 'gram') 
                            ? `${(s.aggregated_qty_needed / 1000).toFixed(2)} ${s.unit === 'ml' ? 'L' : 'kg'}` 
                            : `${s.aggregated_qty_needed} ${s.unit}`;
                          return (
                            <li key={idx} className="flex justify-between items-center text-sm">
                              <span className="text-[#302621] font-medium flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                                {s.item_name || s.name}
                              </span>
                              <span className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold shadow-sm">
                                Beli {displayQty}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Deadline Mendekat */}
            {(isProduction || isMarketing) && deadlineAlerts.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-bold text-yellow-800 text-sm uppercase">Tenggat Waktu Kritis</h4>
                </div>
                {deadlineAlerts.map(o => (
                  <div key={`sheet-dl-${o.id}`} className="bg-white border border-yellow-200 rounded-xl p-4 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-yellow-500"></div>
                    <div className="flex justify-between items-center">
                      <div>
                        <h5 className="font-bold text-[#302621]">{o.brand_name}</h5>
                        <p className="text-xs text-muted-foreground mt-0.5">PO #{o.order_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-1">Deadline: {formatDate(o.deadlineDate)}</p>
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-bold ${o.deadlineDate <= in3Days ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {daysLeft(o.deadlineDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Stok Menipis Kategori */}
            {(isProduction || isFinance || isMarketing || isPurchasing) && (lowBahan.length > 0 || lowPackaging.length > 0) && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-5 h-5 text-orange-600" />
                  <h4 className="font-bold text-orange-800 text-sm uppercase">Peringatan Stok Gudang</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {lowBahan.map(i => (
                    <div key={`sheet-bahan-${i.id}`} className="bg-white border border-blue-200 rounded-xl p-3 shadow-sm hover:border-blue-400 transition-colors cursor-pointer" onClick={() => goToStok('bahan-cair')}>
                      <div className="flex items-start gap-2">
                        <Droplets className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-blue-600 font-bold uppercase">{i.kategori}</p>
                          <p className="font-semibold text-sm text-[#302621] mt-0.5 line-clamp-1">{i.nama}</p>
                          <p className={`text-xs mt-1 font-medium ${i.stok <= 0 ? 'text-red-600' : 'text-blue-700'}`}>
                            {i.stok <= 0 ? 'Stok Habis!' : `Tersisa: ${i.stok} ${i.satuan || 'kg'}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {lowPackaging.map(i => (
                    <div key={`sheet-pack-${i.id}${i._tipe}`} className="bg-white border border-orange-200 rounded-xl p-3 shadow-sm hover:border-orange-400 transition-colors cursor-pointer" onClick={() => goToStok(i._tipe)}>
                      <div className="flex items-start gap-2">
                        <Package className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-orange-600 font-bold uppercase">{i._tipe}</p>
                          <p className="font-semibold text-sm text-[#302621] mt-0.5 line-clamp-1">{i.nama}</p>
                          <p className={`text-xs mt-1 font-medium ${i.stok <= 0 ? 'text-red-600' : 'text-orange-700'}`}>
                            {i.stok <= 0 ? 'Stok Habis!' : `Tersisa: ${i.stok} pcs`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {total === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-lg text-[#302621]">Sistem Berjalan Lancar</h4>
                <p className="text-muted-foreground mt-1">Tidak ada peringatan atau kebutuhan aksi dari departemen ini.</p>
              </div>
            )}
            
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}